import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import type { StagedUploadPreview, StagedUploadType, ProcessStagedUploadResponse } from '@/lib/types/uploads.types';

/**
 * Common CSV-upload API — shared by every "upload a file, map its columns,
 * then process it" flow. Talks to the Express middleware at `/api/uploads/*`,
 * which parses the CSV server-side and, on /process, dispatches to whichever
 * backend actually owns that upload's `type` (the enrichment service today).
 */
export const uploadsApi = createApi({
  reducerPath: 'uploadsApi',
  baseQuery: axiosBaseQuery('/api/uploads'),
  endpoints: (builder) => ({
    /** Uploads the raw file; the response carries headers + a sample row for the mapping UI, never the full parsed data. */
    createStagedUpload: builder.mutation<StagedUploadPreview, { file: File; type: StagedUploadType; meta?: Record<string, unknown> }>({
      query: ({ file, type, meta }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        if (meta) formData.append('meta', JSON.stringify(meta));
        // Not '/' — axiosBaseQuery concatenates this onto '/api/uploads' as-is,
        // and a trailing slash gets 308-redirected by Next before it ever
        // reaches Express, turning one request into two (or dropped, depending
        // on how the caller follows redirects).
        return { url: '', method: 'POST', body: formData };
      },
    }),

    /**
     * Saves the column mapping the user chose, ahead of processStagedUpload.
     * `selectedColumns` is workspace-only — the extra (non-LinkedIn) columns
     * to keep, from the full header list `createStagedUpload` returned; the
     * server always keeps the mapped LinkedIn column on top of these.
     *
     * `meta` is merged into whatever was staged at upload time — for anything
     * the user only decides once they can see the file's headers (the
     * workspace import's contact-vs-company choice, the sheet's name).
     */
    updateStagedUploadMapping: builder.mutation<
      StagedUploadPreview,
      {
        uploadId: string;
        mapping: Record<string, string>;
        selectedColumns?: string[];
        meta?: Record<string, unknown>;
      }
    >({
      query: ({ uploadId, mapping, selectedColumns, meta }) => ({
        url: `/${uploadId}`,
        method: 'PATCH',
        body: { mapping, ...(selectedColumns ? { selectedColumns } : {}), ...(meta ? { meta } : {}) },
      }),
    }),

    /** Hands the staged upload off to its destination service and starts processing. */
    processStagedUpload: builder.mutation<ProcessStagedUploadResponse, string>({
      query: (uploadId) => ({ url: `/${uploadId}/process`, method: 'POST' }),
    }),
  }),
});

export const {
  useCreateStagedUploadMutation,
  useUpdateStagedUploadMappingMutation,
  useProcessStagedUploadMutation,
} = uploadsApi;

export default uploadsApi;
